import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarBlank, CalendarPlus, ClipboardText, FolderSimple, PencilSimple, ShieldCheck, Signature, Umbrella, UploadSimple, UsersThree, Wrench,
} from '@phosphor-icons/react';
import { Chip, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, ToastPill, useToastPill } from '../../../../components/immobilien/ui';
import { useIslandActions } from '../../../../components/island/IslandProvider';
import { eur } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import type { DienstleisterLinkZeile, DienstleisterZeile, FachDokumentZeile, TerminZeile, VertragZeile } from '../../../../lib/immobilien/useObjektDetail';
import { TerminFormularKarte } from './TerminFormularKarte';
import { DokumentKachel, DokumentRaster } from './DokumentKachel';

const hatKategorie = (d: DienstleisterZeile, muster: RegExp) => d.kategorien.some(k => muster.test(k.name));
const WARTUNG_KATEGORIEN = /heizung|schornstein|aufzug|elektr|klempner|schädling/i;
const VERSICHERER = /versicher/i;

/** Termin anlegen mit dem bestehenden Termin-Formular (Typ vorbelegt), im neuen Design auf der Seite. */
function useTerminAnlegen(objektId: string, onGespeichert: () => void) {
  const [toast, zeige] = useToastPill();
  const [vorlage, setVorlage] = useState<{ typ: string; titel: string } | null>(null);
  const anlegen = (typ: string, titel: string) => setVorlage({ typ, titel });
  const karte = vorlage ? (
    <TerminFormularKarte termin={null} prefillObjektId={objektId} prefillTyp={vorlage.typ} prefillTitel={vorlage.titel}
      onAbbrechen={() => setVorlage(null)} onGespeichert={() => { setVorlage(null); zeige('Termin angelegt'); onGespeichert(); }} />
  ) : null;
  return { anlegen, toast, karte };
}

function TerminZeilen({ termine, leer }: { termine: readonly TerminZeile[]; leer: string }) {
  const { onSearchNavigate } = useIslandActions();
  if (termine.length === 0) return <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{leer}</span>;
  return (
    <div className="flex flex-col" style={{ gap: 4 }}>
      {termine.map(t => (
        <button key={t.id} type="button" onClick={() => onSearchNavigate?.('/admin/kalender', t.id, 'termin')}
          className="immo-hover-flaeche flex items-center text-left cursor-pointer" title="Im Kalender zeigen"
          style={{ gap: 10, padding: '8px 8px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
          <CalendarBlank size={16} color="var(--acc)" />
          <span className="immo-zahl shrink-0" style={{ width: 84, fontSize: 12, color: 'var(--ink3)' }}>{deDatum(t.start_datum)}</span>
          <span className="truncate flex-1" style={{ fontSize: 14, fontWeight: 600 }}>{t.titel || 'Termin'}</span>
        </button>
      ))}
    </div>
  );
}

function DienstleisterZeilen({ liste, leer }: { liste: readonly DienstleisterZeile[]; leer: string }) {
  if (liste.length === 0) return <span style={{ fontSize: 13, color: 'var(--ink3)' }}>{leer}</span>;
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {liste.map(d => (
        <div key={d.id} className="flex items-center min-w-0" style={{ gap: 10 }}>
          <Wrench weight="fill" size={15} color="var(--acc)" />
          <span className="truncate flex-1" style={{ fontSize: 14, fontWeight: 700 }}>{d.name}</span>
          <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)', maxWidth: '50%' }}>{d.kategorien.map(k => k.name).join(', ')}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * „Wartung & Prüfungen“: letzte Prüfung (Stammdaten), Wartungstermine, Wartungsdokumente und
 * zuständige Dienstleister. Ein Register der Prüfpflichten gibt es noch nicht (Fehlende Daten).
 */
export function WartungBereich({ objektId, letztePruefung, termine, dokumente, dienstleister, heute, onTerminGeaendert, onPruefungAendern, fuerEinheit = false }: {
  objektId: string;
  letztePruefung: string | null;
  termine: readonly TerminZeile[];
  dokumente: readonly FachDokumentZeile[];
  dienstleister: readonly DienstleisterZeile[];
  heute: string;
  onTerminGeaendert: () => void;
  onPruefungAendern: () => void;
  fuerEinheit?: boolean;
}) {
  const navigate = useNavigate();
  const { anlegen, toast, karte } = useTerminAnlegen(objektId, onTerminGeaendert);
  const wartung = termine.filter(t => t.typ === 'wartung' && (t.status ?? '') !== 'abgesagt');
  const kommend = wartung.filter(t => t.start_datum.slice(0, 10) >= heute);
  const vergangen = wartung.filter(t => t.start_datum.slice(0, 10) < heute).reverse();
  const docs = dokumente.filter(d => d.kategorie === 'Wartung');
  const dl = dienstleister.filter(d => hatKategorie(d, WARTUNG_KATEGORIEN));
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {karte}
      {fuerEinheit && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Prüfungen und Wartung werden je Objekt geführt und gelten für alle Einheiten.</span>}
      <KpiReihe>
        <KpiTile label="Letzte Prüfung" wert={letztePruefung ? deDatum(letztePruefung) : '–'} sub="laut Stammdaten" icon={ShieldCheck} ton="acc" />
        <KpiTile label="Nächste Wartung" wert={kommend[0] ? deDatum(kommend[0].start_datum) : '–'} sub={kommend[0]?.titel || 'kein Termin geplant'} icon={CalendarBlank} />
        <KpiTile label="Wartungsdokumente" wert={docs.length} sub="Kategorie „Wartung“" icon={FolderSimple} />
      </KpiReihe>
      <InnenKarte>
        <KartenTitel icon={CalendarBlank}>Wartungstermine</KartenTitel>
        <TerminZeilen termine={[...kommend, ...vergangen.slice(0, 5)]} leer="Noch keine Wartungstermine." />
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton art="akzent" icon={CalendarPlus} onClick={() => { anlegen('wartung', 'Wartung'); }}>Wartung planen</ImmoButton>
          <ImmoButton icon={PencilSimple} onClick={onPruefungAendern}>{fuerEinheit ? 'Letzte Prüfung im Objekt ändern' : 'Letzte Prüfung ändern'}</ImmoButton>
        </div>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={FolderSimple} rechts={docs.length ? `${docs.length}` : undefined}>Wartungsdokumente</KartenTitel>
        {docs.length === 0 ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Dokumente mit der Kategorie „Wartung“.</span> : (
          <DokumentRaster>{docs.slice(0, 12).map(d => <DokumentKachel key={d.id} d={d} />)}</DokumentRaster>
        )}
        <ImmoButton icon={UploadSimple} onClick={() => navigate(`/admin/dokumente?objekt=${encodeURIComponent(objektId)}`)} style={{ alignSelf: 'flex-start' }}>Im Dokumentenbereich hochladen</ImmoButton>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={Wrench}>Zuständige Fachbetriebe</KartenTitel>
        <DienstleisterZeilen liste={dl} leer="Kein Heizungs-, Schornstein-, Aufzugs- oder Elektrobetrieb zugeordnet." />
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={ClipboardText}>Prüfpflichten</KartenTitel>
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Ein Register der Prüfpflichten mit Intervallen (z. B. Rauchmelder, Legionellen, Aufzug) wird noch angebunden – dafür fehlt eine Tabelle.</span>
      </InnenKarte>
    </>
  );
}

/** „Versicherungen“: Versicherungsdokumente und zugeordnete Versicherer. Eine Policenverwaltung fehlt noch. */
export function VersicherungBereich({ objektId, dokumente, dienstleister, fuerEinheit = false }: {
  objektId: string; dokumente: readonly FachDokumentZeile[]; dienstleister: readonly DienstleisterZeile[]; fuerEinheit?: boolean;
}) {
  const navigate = useNavigate();
  const docs = dokumente.filter(d => d.kategorie === 'Versicherung');
  const versicherer = dienstleister.filter(d => hatKategorie(d, VERSICHERER));
  return (
    <>
      {fuerEinheit && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Gebäudeversicherungen gelten für das ganze Objekt.</span>}
      <InnenKarte>
        <KartenTitel icon={Umbrella} rechts={docs.length ? `${docs.length} Dokumente` : undefined}>Versicherungsdokumente</KartenTitel>
        {docs.length === 0 ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Dokumente mit der Kategorie „Versicherung“.</span> : (
          <DokumentRaster>{docs.map(d => <DokumentKachel key={d.id} d={d} />)}</DokumentRaster>
        )}
        <ImmoButton icon={UploadSimple} onClick={() => navigate(`/admin/dokumente?objekt=${encodeURIComponent(objektId)}`)} style={{ alignSelf: 'flex-start' }}>Im Dokumentenbereich hochladen</ImmoButton>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={Wrench}>Versicherer</KartenTitel>
        <DienstleisterZeilen liste={versicherer} leer="Kein Haftpflicht- oder Sachversicherer zugeordnet (Dienstleister des Objekts)." />
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={ShieldCheck}>Policen</KartenTitel>
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Versicherungsart, Policennummer, Beitrag und Ablauf werden noch angebunden – dafür fehlt eine Tabelle.</span>
      </InnenKarte>
    </>
  );
}

const VERTRAG_STATUS: Record<string, { ton: 'ok' | 'warn' | 'neutral' | 'acc'; text: string }> = {
  aktiv: { ton: 'ok', text: 'Aktiv' }, entwurf: { ton: 'neutral', text: 'Entwurf' }, gekuendigt: { ton: 'warn', text: 'Gekündigt' }, ausgelaufen: { ton: 'neutral', text: 'Ausgelaufen' },
};

/**
 * „Verträge“: Mietverträge aus dem Vertragsbereich und die Verträge mit Dienstleistern
 * (Zuordnung Dienstleister ↔ Objekt). Kostenanteil je Einheit fehlt noch (Fehlende Daten).
 */
export function VertraegeBereich({ vertraege, dienstleister, links, mieterName, onDienstleister, fuerEinheit = false }: {
  vertraege: readonly VertragZeile[];
  dienstleister: readonly DienstleisterZeile[];
  links: readonly DienstleisterLinkZeile[];
  mieterName: (id: string | null) => string;
  onDienstleister: () => void;
  fuerEinheit?: boolean;
}) {
  const navigate = useNavigate();
  const aktiv = vertraege.filter(v => v.status === 'aktiv');
  return (
    <>
      <KpiReihe>
        <KpiTile label="Mietverträge" wert={vertraege.length} sub={`${aktiv.length} aktiv`} icon={Signature} ton="acc" />
        <KpiTile label="Dienstleister-Verträge" wert={dienstleister.length} sub={fuerEinheit ? 'des Objekts' : 'zugeordnet'} icon={Wrench} />
      </KpiReihe>
      <InnenKarte>
        <KartenTitel icon={Signature}>Mietverträge</KartenTitel>
        {vertraege.length === 0 ? (
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Im Vertragsbereich ist {fuerEinheit ? 'für diese Einheit' : 'für dieses Objekt'} noch kein Mietvertrag angelegt. Mietbeginn, Miete und Kaution stehen im Mietverhältnis.</span>
        ) : (
          <div className="flex flex-col" style={{ gap: 4 }}>
            {vertraege.map(v => {
              const st = VERTRAG_STATUS[v.status ?? ''] ?? { ton: 'neutral' as const, text: v.status || '–' };
              return (
                <button key={v.id} type="button" onClick={() => navigate(`/admin/vertraege/${encodeURIComponent(v.id)}`)}
                  className="immo-hover-flaeche flex items-center text-left cursor-pointer flex-wrap"
                  style={{ gap: 10, padding: '10px 8px', borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
                  <span className="flex flex-col flex-1 min-w-0" style={{ flexBasis: 200 }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{mieterName(v.mieter_id)}{v.einheit && !fuerEinheit ? ` · ${v.einheit}` : ''}</span>
                    <span className="truncate immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                      {[v.vertragsnummer, v.beginn ? `ab ${deDatum(v.beginn)}` : '', v.unbefristet ? 'unbefristet' : v.ende ? `bis ${deDatum(v.ende)}` : '', v.kuendigungsfrist_monate ? `Kündigungsfrist ${v.kuendigungsfrist_monate} Mon.` : ''].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {v.mietanpassung_typ && v.mietanpassung_typ !== 'keine' && <Chip groesse="xs" ton="info">{v.mietanpassung_typ === 'staffel' ? 'Staffelmiete' : 'Indexmiete'}</Chip>}
                  {v.kaltmiete != null && <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 800 }}>{eur(v.kaltmiete)}</span>}
                  <Chip groesse="xs" ton={st.ton}>{st.text}</Chip>
                </button>
              );
            })}
          </div>
        )}
        <ImmoButton icon={Signature} onClick={() => navigate('/admin/vertraege')} style={{ alignSelf: 'flex-start' }}>Zum Vertragsbereich</ImmoButton>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={Wrench}>Dienstleister</KartenTitel>
        {dienstleister.length === 0 ? <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Keine Dienstleister zugeordnet.</span> : (
          <div className="flex flex-col" style={{ gap: 8 }}>
            {dienstleister.map(d => {
              const link = links.find(l => l.dienstleister_id === d.id);
              return (
              <div key={d.id} className="flex items-center flex-wrap" style={{ gap: 10 }}>
                <Wrench weight="fill" size={15} color="var(--acc)" />
                <span className="truncate flex-1" style={{ fontSize: 14, fontWeight: 700, minWidth: 140 }}>{d.name}</span>
                <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
                  {[link?.vertragsart, link?.von ? `seit ${deDatum(link.von)}` : '', link?.bis ? `bis ${deDatum(link.bis)}` : ''].filter(Boolean).join(' · ') || 'ohne Vertragsangaben'}
                </span>
              </div>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
          <ImmoButton icon={PencilSimple} onClick={onDienstleister}>{fuerEinheit ? 'Im Objekt verwalten' : 'Dienstleister verwalten'}</ImmoButton>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Kostenanteil je Einheit: noch nicht angebunden</span>
        </div>
      </InnenKarte>
    </>
  );
}

/**
 * „Eigentümerversammlung & Beschlüsse“ (WEG): Versammlungen sind Termine vom Typ „Meeting“
 * (Zuordnung Termin-Typen, freigegeben 11.10.). Beschlusssammlung fehlt noch (Fehlende Daten).
 */
export function EtvBereich({ objektId, termine, heute, onTerminGeaendert, fuerEinheit = false }: {
  objektId: string; termine: readonly TerminZeile[]; heute: string; onTerminGeaendert: () => void; fuerEinheit?: boolean;
}) {
  const { anlegen, toast, karte } = useTerminAnlegen(objektId, onTerminGeaendert);
  const versammlungen = termine.filter(t => t.typ === 'meeting' && (t.status ?? '') !== 'abgesagt');
  const kommend = versammlungen.filter(t => t.start_datum.slice(0, 10) >= heute);
  const vergangen = versammlungen.filter(t => t.start_datum.slice(0, 10) < heute).reverse();
  if (versammlungen.length === 0) {
    return (
      <>
        {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
        {karte}
        <LeerZustand icon={UsersThree} titel="Keine Versammlung geplant" text="Versammlungen werden als Termin im Kalender geführt.">
          <ImmoButton art="akzent" icon={CalendarPlus} onClick={() => { anlegen('meeting', 'Eigentümerversammlung'); }}>Versammlung planen</ImmoButton>
        </LeerZustand>
      </>
    );
  }
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {karte}
      {fuerEinheit && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Versammlungen gelten für die ganze Gemeinschaft.</span>}
      <KpiReihe>
        <KpiTile label="Nächste Versammlung" wert={kommend[0] ? deDatum(kommend[0].start_datum) : '–'} sub={kommend[0]?.titel || 'keine geplant'} icon={UsersThree} ton="acc" />
        <KpiTile label="Letzte Versammlung" wert={vergangen[0] ? deDatum(vergangen[0].start_datum) : '–'} sub={vergangen[0]?.titel || '–'} icon={CalendarBlank} />
      </KpiReihe>
      <InnenKarte>
        <KartenTitel icon={CalendarBlank}>Versammlungen</KartenTitel>
        <TerminZeilen termine={[...kommend, ...vergangen]} leer="Keine Versammlungen." />
        <ImmoButton art="akzent" icon={CalendarPlus} onClick={() => { anlegen('meeting', 'Eigentümerversammlung'); }} style={{ alignSelf: 'flex-start' }}>Versammlung planen</ImmoButton>
      </InnenKarte>
      <InnenKarte>
        <KartenTitel icon={ClipboardText}>Beschlüsse</KartenTitel>
        <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Die Beschlusssammlung (Beschluss, Abstimmung, Umsetzung) wird noch angebunden – dafür fehlt eine Tabelle.</span>
      </InnenKarte>
    </>
  );
}
